// Finance & Books (double-entry) shared helpers.
// Keep in sync with /app/backend/accounting/.
//
// Money crosses the wire as integer centavos (`*_minor`). The UI never does
// arithmetic on pesos: it formats centavos for display and sends the typed
// decimal string straight back, so nothing is silently rounded.

export const RECORDS_LABEL = "Internal Accounting Records";
export const RECORDS_NOTE =
  "These are your business's internal accounting records, prepared so an accountant can review them. " +
  "They are not official books registered with any government agency, and ConfirmPH does not decide tax treatment.";

export const CAPABILITIES = {
  view: "finance.view",
  recordPayment: "finance.record_payment",
  recordExpense: "finance.record_expense",
  depositsCollect: "finance.deposits.collect",
  depositsRefund: "finance.deposits.refund",
  depositsApply: "finance.deposits.apply",
  post: "finance.post",
  correct: "finance.correct",
  closePeriod: "finance.close_period",
  export: "finance.export",
  configure: "finance.configure",
};

export const can = (status, cap) => (status?.capabilities || []).includes(cap);

export function fmtMinor(minor, { sign = false } = {}) {
  const value = Number(minor || 0) / 100;
  const formatted = value.toLocaleString("en-PH", { style: "currency", currency: "PHP",
                                                    minimumFractionDigits: 2 });
  return sign && value > 0 ? `+${formatted}` : formatted;
}

export const minorToInput = (minor) => (Number(minor || 0) / 100).toFixed(2);

export function idemHeaders() {
  const key = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return { "Idempotency-Key": key };
}

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const monthStartISO = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "gcash", label: "GCash" },
  { value: "maya", label: "Maya" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other / not specified" },
];

export const CHARGE_CATEGORIES = [
  { value: "damage", label: "Damage recovery", evidence: true },
  { value: "fuel_shortage", label: "Fuel / charging" },
  { value: "late_return", label: "Late return" },
  { value: "cleaning", label: "Cleaning" },
  { value: "missing_item", label: "Missing item or key", evidence: true },
  { value: "policy_violation", label: "Policy violation" },
  { value: "mileage_overage", label: "Mileage overage" },
  { value: "post_rental", label: "Post-rental (tolls, fines)" },
  { value: "cancellation_fee", label: "Cancellation fee" },
  { value: "no_show_fee", label: "No-show fee" },
  { value: "extension", label: "Extension" },
  { value: "delivery", label: "Delivery / pickup" },
  { value: "additional_driver", label: "Additional driver" },
  { value: "other", label: "Other charge" },
];

export const EXPENSE_CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "repairs", label: "Repairs" },
  { value: "maintenance", label: "Maintenance" },
  { value: "insurance", label: "Insurance" },
  { value: "registration", label: "Registration / LTO" },
  { value: "cleaning", label: "Cleaning" },
  { value: "driver_compensation", label: "Driver compensation" },
  { value: "staff", label: "Staff costs" },
  { value: "tolls_parking_fines", label: "Tolls, parking, fines" },
  { value: "marketing", label: "Marketing" },
  { value: "vehicle_other", label: "Other vehicle cost" },
  { value: "bank_fees", label: "Bank / payment fees" },
  { value: "other", label: "Other operating cost" },
];

export const DOC_TYPE_LABELS = {
  rental_charge: "Rental charge",
  customer_charge: "Customer charge",
  credit_note: "Credit note",
  payment: "Payment received",
  advance_payment: "Advance payment",
  deposit_event: "Deposit",
  deposit_application: "Deposit applied",
  application: "Advance applied",
  expense: "Expense",
  bill: "Vendor bill",
  bill_payment: "Bill payment",
  driver_accrual: "Driver pay owed",
  driver_payment: "Driver pay paid",
  transfer: "Transfer",
  write_off: "Write-off",
  other_income: "Other income",
  financing: "Owner / loan",
  insurance_claim: "Insurance claim",
  insurance_receipt: "Insurer payment",
  opening_balance: "Opening balances",
  opening_receivable: "Opening receivable",
  opening_payable: "Opening payable",
  historical_import: "Historical import",
  manual_journal: "Manual journal",
};

export const docTypeLabel = (type) => DOC_TYPE_LABELS[type] || type;

export const STATUS_TONES = {
  posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reversed: "bg-slate-100 text-slate-600 border-slate-200",
  ready_to_post: "bg-amber-50 text-amber-700 border-amber-200",
  expected: "bg-sky-50 text-sky-700 border-sky-200",
  voided: "bg-slate-100 text-slate-500 border-slate-200",
  pending_approval: "bg-amber-50 text-amber-700 border-amber-200",
  OPEN: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CLOSED: "bg-slate-100 text-slate-700 border-slate-300",
  LOCKED: "bg-rose-50 text-rose-700 border-rose-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
};

export const tone = (key) => STATUS_TONES[key] || "bg-slate-100 text-slate-600 border-slate-200";

export async function downloadFile(path, filename) {
  const token = localStorage.getItem("confirmph_token");
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`,
                               { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
